import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { trackingService } from '../../services/tracking.service';
import { dayBounds } from '../../utils/timezone';
import { flowsService } from '../../services/flows.service';
import { landingPagesService } from '../../services/landing-pages.service';
import { offersService } from '../../services/offers.service';
import { labelForField, NIL_UUID } from './tracking-page.format';
import { ENTITY_FILTER_ORDER } from '../../components/entity-fetchers';

/** The row slice a right-click landed on, plus where to anchor the menu. */
export interface RowContext {
  mouseX: number;
  mouseY: number;
  field: string;
  value: string;
  label: string;
}

/** Dimensions whose entity has a URL worth copying straight off the report. */
export const COPYABLE_URL: Record<string, { label: string; get: (id: string) => Promise<string> }> = {
  flow_id: {
    label: 'flow URL',
    get: async (id) => {
      const urls = await flowsService.getTrackingUrls(id);
      const firstDomain = urls.available_domains?.[0] || Object.keys(urls.domain_urls)[0];
      return urls.domain_urls[firstDomain]?.campaign_url || '';
    },
  },
  offer_id: {
    label: 'offer URL',
    get: async (id) => (await offersService.getById(id)).url || '',
  },
  landing_page_id: {
    label: 'landing page URL',
    get: async (id) => (await landingPagesService.getById(id)).url || '',
  },
};

export const DUPLICABLE = new Set([
  'offer_id', 'flow_id', 'landing_page_id', 'network_id', 'traffic_source_id',
]);

// `(none)` is real traffic with no entity: the report coalesces the missing id to the nil UUID.
export const hasEntity = (ctx: RowContext | null): boolean =>
    !!ctx?.value && ctx.value !== NIL_UUID;

interface LeadBreakdownState {
  label: string;
  loading: boolean;
  data: Awaited<ReturnType<typeof trackingService.getLeadBreakdown>> | null;
}

/**
 * What the right-click menu can do with a row: pivot into the lead attributes behind
 * its conversions, copy its entity's URL, or open the click/conversion log for it.
 * Each carries the report's current window so the result matches the number clicked.
 */
export function useRowActions({ dateFrom, dateTo, timezone, filterParams, tagIds }: {
  dateFrom: string;
  dateTo: string;
  timezone: number;
  filterParams: Record<string, string | string[]>;
  tagIds: string[];
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [contextMenu, setContextMenu] = useState<RowContext | null>(null);
  const [leadBreakdown, setLeadBreakdown] = useState<LeadBreakdownState | null>(null);

  /**
   * Lead attributes (age/home/insured/…) for the right-clicked slice, over the report's
   * current window. The values ride inside each conversion's postback URL, so this reads
   * them server-side rather than needing new columns.
   */
  const openLeadBreakdown = useCallback(async () => {
    if (!contextMenu) return;
    const { field, value, label } = contextMenu;
    setContextMenu(null);
    setLeadBreakdown({ label: `${labelForField(field)}: ${label}`, loading: true, data: null });
    try {
      const data = await trackingService.getLeadBreakdown({
        ...dayBounds(dateFrom, dateTo),
        timezone,
        ...filterParams,
        ...(tagIds.length ? { tags: tagIds } : {}),
        // the right-clicked slice itself; '' is the real "(none)" bucket
        ...(field && field !== 'date' ? { [field]: value ?? '' } : {}),
        ...(field === 'date' ? dayBounds(value, value) : {}),
      });
      setLeadBreakdown({ label: `${labelForField(field)}: ${label}`, loading: false, data });
    } catch {
      enqueueSnackbar('Could not load the lead breakdown', { variant: 'error' });
      setLeadBreakdown(null);
    }
  }, [contextMenu, dateFrom, dateTo, timezone, filterParams, tagIds, enqueueSnackbar]);

  const copyEntityUrl = useCallback(async () => {
    const spec = contextMenu && COPYABLE_URL[contextMenu.field];
    if (!spec || !contextMenu?.value) return;
    const id = contextMenu.value;
    setContextMenu(null);
    let url: string;
    try {
      url = await spec.get(id);
      if (!url) throw new Error('empty');
    } catch {
      enqueueSnackbar(`Could not load the ${spec.label}`, { variant: 'error' });
      return;
    }
    // Separate from the fetch: the clipboard refuses without focus, and reporting that
    // as a failed load sends you looking in the wrong place.
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar(`Copied ${spec.label}`, { variant: 'success', autoHideDuration: 1500 });
    } catch {
      enqueueSnackbar(`Could not copy — ${url}`, { variant: 'warning', autoHideDuration: 6000 });
    }
  }, [contextMenu, enqueueSnackbar]);

  /**
   * Open the click/conversion log for the right-clicked row, carrying this report's
   * window + the row's own slice. Both log pages read their state from the URL, so the
   * result is a shareable link to exactly the rows behind a number.
   */
  const openRowLog = useCallback((path: '/clicks' | '/conversions') => {
    if (!contextMenu) return;
    const { field, value, label } = contextMenu;
    const p = new URLSearchParams();
    p.set('dates', `${dateFrom}..${dateTo}`);
    p.set('tz', String(timezone));
    if (tagIds.length) p.set('tags', JSON.stringify(tagIds));

    if (ENTITY_FILTER_ORDER.includes(field) && value) {
      p.set('filters', JSON.stringify([{ field, value, label: label || value }]));
      p.set('ffield', field);
    } else if (field === 'date') {
      p.set('dates', `${value}..${value}`);
    } else {
      // '' is the real "(none)" bucket, so send it rather than dropping it.
      p.set('dims', JSON.stringify({ [field]: value ?? '' }));
    }
    setContextMenu(null);
    navigate(`${path}?${p.toString()}`);
  }, [contextMenu, dateFrom, dateTo, timezone, tagIds, navigate]);

  return {
    contextMenu, setContextMenu,
    leadBreakdown, setLeadBreakdown,
    openLeadBreakdown, copyEntityUrl, openRowLog,
  };
}
